import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';

import { DesktopAppService } from '@intranet/core/services/desktop-app.service';
import { TimerGapCoordinatorService } from '@intranet/core/services/timer-gap-coordinator.service';
import { TimerSyncService } from '@intranet/core/services/timer-sync.service';
import { TimerEntryService } from '@intranet/modules/timer/timer-entry.service';
import { TimeEventService } from '@intranet/modules/timer/time-event.service';
import { TimerToastService } from '@intranet/modules/timer/timer-toast/timer-toast.service';

/**
 * Mirror of `idle-resolution-modal` for the "your app died with a timer
 * running" case. The three resolution actions are functionally identical
 * (keep / push start forward / stop at the gap's beginning) — the wording
 * is what's different, because this isn't about idleness but about the
 * app not being running at all.
 *
 * Mounted only in the `desktop-shell` so it never appears on the regular
 * browser intranet pages.
 */
@Component({
    selector: 'crash-recovery-modal',
    templateUrl: './crash-recovery-modal.component.html',
    styleUrls: ['./crash-recovery-modal.component.scss'],
})
export class CrashRecoveryModalComponent implements OnInit, OnDestroy {
    isOpen = false;
    crashAt!: Date;
    gapMs = 0;

    private sub?: Subscription;

    constructor(
        private coordinator: TimerGapCoordinatorService,
        private desktopApp: DesktopAppService,
        private timerEntry: TimerEntryService,
        private timeEvent: TimeEventService,
        private timerSync: TimerSyncService,
        private toastService: TimerToastService,
    ) {}

    /** Title text — different wording for the desktop app vs the website
     *  ("the app was closed" vs "you were away") since "app" doesn't fit
     *  a browser context. */
    get titleText(): string {
        return this.desktopApp.isTimerApp
            ? 'Your timer kept running while the app was closed'
            : 'Your timer kept running while you were away';
    }

    /** Inline phrase used in the body — describes what was "not running". */
    get bodyDescriptor(): string {
        return this.desktopApp.isTimerApp
            ? "the app wasn't running"
            : "the page wasn't open";
    }

    ngOnInit(): void {
        // Subscribe to the shared coordinator so idle and crash prompts
        // are shown strictly one at a time, never overlapping.
        this.sub = this.coordinator.activeGap$.subscribe(event => {
            if (event && event.kind === 'crash'
                && this.timerEntry.isLiveTimerRunning
                && this.timerEntry.liveStartTime) {
                this.crashAt = new Date(event.startedAt);
                this.gapMs = event.durationMs;
                this.isOpen = true;
            } else {
                this.isOpen = false;
            }
        });
    }

    ngOnDestroy(): void {
        this.sub?.unsubscribe();
    }

    /** Keep the gap as part of the entry — no API or local state change. */
    keepTime(): void {
        this.coordinator.resolve();
    }

    /** Push the running timer's start forward by the gap so the offline
     *  period is excluded from the entry's duration. */
    discardTime(): void {
        // The timer may have been stopped elsewhere between the recovery
        // event arriving and the user clicking. Bail silently — there's
        // no running entry left to adjust.
        if (!this.timerEntry.isLiveTimerRunning) {
            this.coordinator.resolve();
            return;
        }
        const oldStart = this.timerEntry.liveStartTime;
        if (!oldStart) {
            this.coordinator.resolve();
            return;
        }
        const newStart = new Date(oldStart.getTime() + this.gapMs);

        // Update local state first so the UI reflects the new duration
        // immediately, then persist to the backend.
        this.timerEntry.updateStartTime(newStart);
        this.timeEvent
            .updateRunningTimerStartTime(newStart.toISOString())
            .subscribe({
                next: () => this.timerSync.notify('timer-changed'),
                // Roll back the local timer to the pre-discard start so it
                // matches what the server still has — without this the
                // "discarded" gap would silently come back on reload.
                error: () => {
                    this.timerEntry.updateStartTime(oldStart);
                    this.toastService.show('Could not discard the time — please try again.');
                },
            });

        this.coordinator.resolve();
    }

    /** Stop the timer and set its end time to the moment the app crashed
     *  — anything after that point is excluded from the entry. */
    stopAtCrash(): void {
        if (!this.timerEntry.isLiveTimerRunning) {
            this.coordinator.resolve();
            return;
        }
        const crashIso = this.crashAt.toISOString();
        // Pessimistic stop: clear the local timer only once the server has
        // confirmed. The previous optimistic ordering called `stopEntry()`
        // before the API responded, so an offline stop would clear the UI
        // even though the server still had the timer running.
        this.timeEvent.stopTimer().subscribe({
            next: res => {
                if (res?.success && res.stoppedEvent?.timeEventId) {
                    this.timeEvent
                        .updateTimeEvent(res.stoppedEvent.timeEventId, {
                            startTime: res.stoppedEvent.startTime,
                            endTime: crashIso,
                        })
                        .subscribe({
                            next: () => this.timerSync.notify('entries-changed'),
                            // The stop succeeded but adjusting the end failed.
                            // The entry is saved, just with the full duration
                            // including the crash gap — flag it for the user.
                            error: () => this.toastService.show('Stopped the timer but could not trim the gap — please edit the entry manually.'),
                        });
                    this.timerEntry.stopEntry();
                } else {
                    this.toastService.show('Could not stop the timer — please try again.');
                }
            },
            error: () => {
                this.toastService.show('Could not stop the timer — please try again.');
            },
        });
        this.coordinator.resolve();
    }

    formatDuration(ms: number): string {
        const totalMin = Math.max(1, Math.round(ms / 60000));
        if (totalMin < 60) return `${totalMin} minute${totalMin === 1 ? '' : 's'}`;
        const h = Math.floor(totalMin / 60);
        const m = totalMin % 60;
        if (m === 0) return `${h} hour${h === 1 ? '' : 's'}`;
        return `${h}h ${m}m`;
    }

    formatTime(date: Date): string {
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
}
